import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { FileExtensionHelper } from '../_models/file-extensions.enum';
import { SKIP_ERROR_TOAST } from '../shared/http-context-tokens';

/**
 * Service to handle PDF SAS token replacement before opening/downloading PDFs
 */
@Injectable({
  providedIn: 'root'
})
export class PdfSasService {
  private readonly API_URL = `${environment.BASE_URL}/sassearchandreplace`;
  private processingCache = new Map<string, Observable<boolean>>();

  constructor(private http: HttpClient) {}

  /**
   * Processes a PDF URL to ensure it has valid SAS tokens in its embedded links
   * @param pdfUrl The URL of the PDF to process
   * @returns Observable that emits true when processing is complete, false if failed or not a PDF
   */
  processPdfSasTokens(pdfUrl: string): Observable<boolean> {
    // Check if URL is valid and is a PDF
    if (!pdfUrl || !FileExtensionHelper.isPdf(pdfUrl)) {
      return of(true); // Not a PDF, no processing needed
    }

    // Check if this URL is already being processed
    const cacheKey = this.getCacheKey(pdfUrl);
    if (this.processingCache.has(cacheKey)) {
      return this.processingCache.get(cacheKey)!;
    }

    // Create the processing observable
    // Send as an object - ASP.NET Core has issues with [FromBody] string
    const processing$ = this.http.post(
      this.API_URL,
      { blobPath: pdfUrl },
      {
        responseType: 'text',
        context: new HttpContext().set(SKIP_ERROR_TOAST, true)
      }
    ).pipe(
      map((response: string) => {
        return true;
      }),
      catchError((error) => {
        console.error('PdfSasService: Error processing PDF SAS tokens:', error);
        // If processing fails, we still allow the PDF to be opened
        // The backend may have already processed it, or it may not need processing
        return of(true);
      }),
      tap(() => {
        // Remove from cache after processing completes
        this.processingCache.delete(cacheKey);
      })
    );

    // Cache the observable
    this.processingCache.set(cacheKey, processing$);

    return processing$;
  }

  /**
   * Gets a cache key for a PDF URL (strips query parameters for consistency)
   * @param url The PDF URL
   * @returns A normalized cache key
   */
  private getCacheKey(url: string): string {
    try {
      const urlObj = new URL(url);
      return `${urlObj.origin}${urlObj.pathname}`;
    } catch {
      // If URL parsing fails, use the original URL
      return url.split('?')[0];
    }
  }

  /**
   * Checks if a URL is a PDF that needs processing
   * @param url The URL to check
   * @returns True if it's a PDF URL
   */
  isPdfUrl(url: string): boolean {
    return FileExtensionHelper.isPdf(url);
  }
}

